import { AddressAutofillRetrieveResponse } from "@mapbox/search-js-core"
import { AddressAutofill } from "@mapbox/search-js-react"
import { Config } from "@modules/config"
import { word } from "@modules/core/utils/i18n"
import { useCountriesListArray } from "@modules/language/utils/countries"
import { ProfileFormTabProps } from "@modules/profile/myProfile/profileFormikType"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUInput } from "@modules/ui/components/huInput"
import { HUText } from "@modules/ui/components/huText"
import { HURow } from "@modules/ui/components/layout"
import { classNames } from "primereact/utils"
import { useEffect } from "react"
import { useTheme } from "styled-components"

export const ContactForm: React.FC<ProfileFormTabProps> = ({ isFormFieldInValid, getFormErrorMessage, formik }) => {
  const countriesOptions = useCountriesListArray()
  const theme = useTheme()

  useEffect(() => {
    // Init the phone country code with address' country code
    if (formik.values.country && !formik.values.phoneNumberCountryCode)
      formik.setFieldValue("phoneNumberCountryCode", formik.values.country)
  }, [formik.values.country])

  return (
    <div className="flex flex-grow-1 flex-column justify-content-start mt-6 gap-2 px-4">
      <HUText fontStyle="TM" className="align-self-center">
        {word("profile.form.contact.title")}
      </HUText>

      <HUText
        fontStyle="BS"
        className="text-center mb-4"
        style={{ whiteSpace: "pre-line", overflowWrap: "break-word" }}
        color={theme.neutralColor600}
      >
        {word("profile.form.contact.subtitle")}
      </HUText>

      {/* Email input */}
      <FormikInput
        disabled
        label={word("profile.form.mail.label")}
        getFieldProps={formik.getFieldProps}
        isInvalid={isFormFieldInValid("email")}
        name="email"
        error={getFormErrorMessage("email")}
        textProps={{ className: "flex-1" }}
      />

      <AddressAutofill
        accessToken={Config.MAPBOX_API_KEY}
        onRetrieve={(res: AddressAutofillRetrieveResponse) => {
          const foundCountry = countriesOptions.find(
            (countryOption) => countryOption.code === res.features[0].properties.country_code?.toUpperCase(),
          )
          if (foundCountry) formik.setFieldValue("country", foundCountry.code)
        }}
        theme={{
          variables: {
            unitHeader: "28px",
            fontFamily: '"Trenda", sans-serif',
            borderRadius: "0",
            boxShadow: "0 0 1px 1px rgba(0,0,0,0.1)",
            colorText: theme.primaryTextColor,
            colorPrimary: theme.primaryColor,
            colorSecondary: theme.secondaryColor,
            colorBackgroundHover: theme.neutralColor300,
            colorBackground: theme.neutralColor100,
          },
          cssText: `
            .Results {
                z-index: 100000;
                border-radius: 20px;
            }
            .ResultsAttribution {
                  display: none;
                }
        `,
        }}
        popoverOptions={{ offset: 0 }}
      >
        {/* Address input */}
        <>
          <HUText
            type="label"
            color={theme.inputLabelColor}
            fontStyle="LS"
            className={classNames({ "p-error": isFormFieldInValid("address") })}
            textProps={{ htmlFor: "address" }}
          >
            {word("box.form.address.address.label").toUpperCase()}
          </HUText>
          <HUInput
            value={formik.values.address}
            onChange={(event) => {
              formik.setFieldValue("address", event.target.value)
            }}
            autoComplete="address-line1"
            hasError={isFormFieldInValid("address")}
            name="address"
            className="mb-3 mt-2"
            placeholder={word("box.form.address.address.label")}
            onBlur={(e) => {
              if (formik.handleBlur) formik.handleBlur(e, "address")
            }}
          />
        </>
      </AddressAutofill>

      <HURow gap={20}>
        {/* Postal code input */}
        <FormikInput
          flex
          label={word("box.form.address.postalCode.label")}
          isInvalid={isFormFieldInValid("postalCode")}
          getFieldProps={formik.getFieldProps}
          name="postalCode"
          error={getFormErrorMessage("postalCode")}
          textProps={{
            className: "flex-1",
            autoComplete: "postal-code",
          }}
        />

        {/* City input */}
        <FormikInput
          flex
          label={word("box.form.address.city.label")}
          isInvalid={isFormFieldInValid("city")}
          getFieldProps={formik.getFieldProps}
          name="city"
          error={getFormErrorMessage("city")}
          textProps={{
            className: "flex-1",
            autoComplete: "address-level2",
          }}
        />
      </HURow>

      {/* Country input */}
      <FormikInput
        flex
        type="autocomplete"
        label={word("box.form.address.country.label")}
        isInvalid={isFormFieldInValid("country")}
        getFieldProps={formik.getFieldProps}
        setFieldValue={formik.setFieldValue}
        name="country"
        error={getFormErrorMessage("country")}
        placeHolder={word("box.form.address.country.placeholder")}
        autocompleteProps={{
          className: "w-full",
          suggestions: countriesOptions.map((countryOption) => countryOption.label),
          value: countriesOptions.find((countryOption) => countryOption.code === formik.values.country)?.label,
          onSelect: (e) => {
            const foundCountry = countriesOptions.find((countryOption) => countryOption.label === e.value)
            formik.setFieldValue("country", foundCountry?.code)
          },
          onBlur: (e) => {
            if (e.target) {
              const foundCountry = countriesOptions.find((countryOption) => countryOption.label === e.target.value)
              formik.setFieldValue("nationality", foundCountry?.code)
            }

            if (formik.handleBlur) formik.handleBlur(e, "country")
          },
          forceSelection: true,
        }}
      />

      {/* Phone number input */}
      <FormikInput
        label={word("profile.form.phoneNumber.label")}
        isInvalid={isFormFieldInValid("phoneNumber")}
        getFieldProps={formik.getFieldProps}
        name="phoneNumber"
        error={getFormErrorMessage("phoneNumber")}
        type="phoneNumber"
        setFieldValue={formik.setFieldValue}
        placeHolder={word("profile.form.phoneNumber.label")}
        phoneNumberProps={{
          phoneNumber: formik.getFieldProps("phoneNumber").value,
          countryCode: formik.getFieldProps("phoneNumberCountryCode").value,
          onPhoneNumberChange: (e) => {
            formik.setFieldValue("phoneNumber", e.target.value)
            if (formik.handleChange) formik.handleChange(e, "phoneNumber")
          },
          onPhoneCodeChange: (e) => {
            formik.setFieldValue("phoneNumberCountryCode", e.target.value)
          },
        }}
      />

      {/* Emergency contact input */}
      <FormikInput
        type="textarea"
        label={word("profile.form.emergency.label")}
        placeHolder={word("profile.form.emergency.placeholder")}
        textareaProps={{ className: "flex-1", autoResize: true, rows: 3 }}
        getFieldProps={formik.getFieldProps}
        isInvalid={isFormFieldInValid("emergencyInformation")}
        name="emergencyInformation"
        error={getFormErrorMessage("emergencyInformation")}
      />
    </div>
  )
}
